import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  Button,
  Divider,
  Tabs,
  Tab,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import isWeekend from "date-fns/isWeekend";
import isBefore from "date-fns/isBefore";
import startOfToday from "date-fns/startOfToday";
import { format } from "date-fns";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  getAvailableSlot,
  getMeetUserDetails,
} from "../../../actions/availability";
import { v4 as uuidv4 } from "uuid";
import Calendar from "../../../components/presentation/icons/Calendar";
import { getColorProfile } from "../../../utils/getColorProfile";

const MeetingScheduler = ({
  setOpenBigModal,
  userValue,
  queryValue,
  durationValue,
  setTimes,
  setSecondPageStep,
  setDescription,
}) => {
  const dispatch = useDispatch();
  const authUserData = JSON.parse(sessionStorage.getItem("loggedUserData"));
  const current_user = authUserData?.user_data?.invoffuserid;
  const today = startOfToday();
  const [calendarValue, setCalendarValue] = useState(today);
  const [selectedDateIndex, setSelectedDateIndex] = useState(0);
  const [selectedTime, setSelectedTime] = useState(null);

  const daysOfWeek = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const {
    availableSlot,
    availabilityslotloading,
    //to get user role and image only
    meetUserDetail,
    meetUserDetailLoading,
  } = useSelector((state) => state.availability);

  //to get user role and image only
  useEffect(() => {
    if (userValue?.email) {
      dispatch(getMeetUserDetails({ email: userValue?.email }));
    }
  }, [userValue?.email]);

  // Generate valid (future + non-weekend) days in selected month/year
  const dateData = useMemo(() => {
    const result = [];
    const year = calendarValue.getFullYear();
    const month = calendarValue.getMonth();
    const date = new Date(year, month, 1);

    while (date.getMonth() === month) {
      if (!isWeekend(date) && !isBefore(date, today)) {
        result.push({
          day: daysOfWeek[date.getDay()],
          date: format(date, "d"),
          month: months[month],
          fullDate: new Date(date),
          slots: [],
        });
      }
      date.setDate(date.getDate() + 1);
    }

    return result;
  }, [calendarValue]);

  // Auto-select first available day
  useEffect(() => {
    setSelectedDateIndex(0);
    setSelectedTime(dateData[0]?.slots[0] ?? null);
  }, [dateData]);

  const handleDateChange = (e, newIndex) => {
    setSelectedDateIndex(newIndex);
    setSelectedTime(dateData[newIndex]?.slots[0] ?? null);
  };

  const selectedDate = dateData[selectedDateIndex];

  //fetch date slots
  useEffect(() => {
    dispatch({
      type: "GET_AVAILABLE_SLOT",
      payload: [],
    });
    if (
      selectedDate &&
      queryValue?.code &&
      userValue?.user_id &&
      durationValue
    ) {
      const date = moment(selectedDate?.fullDate).format("YYYY-MM-DD");
      const code = queryValue?.code;
      const user_id = userValue?.user_id;
      dispatch(
        getAvailableSlot({
          current_user,
          code,
          date,
          user_id,
          duration: durationValue,
        })
      );
    }
  }, [
    selectedDate?.fullDate,
    queryValue?.code,
    userValue?.user_id,
    durationValue,
  ]);

  //Update selected date slots when availableSlot is fetched
  useEffect(() => {
    if (availableSlot?.data && dateData[selectedDateIndex]) {
      const updatedSlots = availableSlot.data.map((slot) => {
        return {
          id: uuidv4(),
          label: `${moment(slot.start_datetime).format("HH:mm")} - ${moment(
            slot.end_datetime
          ).format("HH:mm")}`,
          start_datetime: slot?.start_datetime,
          end_datetime: slot?.end_datetime,
          meeting_preference: slot?.meeting_preference,
        };
      });
      dateData[selectedDateIndex].slots = updatedSlots;
      setSelectedTime(updatedSlots[0] ?? null);
    } else {
      dateData[selectedDateIndex].slots = [];
      setSelectedTime(null);
    }
  }, [
    JSON.stringify(availableSlot?.data),
    selectedDateIndex,
    selectedDate?.fullDate,
  ]);

  useEffect(() => {
    if (selectedTime) {
      setTimes({
        start_datetime: selectedTime?.start_datetime,
        end_datetime: selectedTime?.end_datetime,
      });
    } else {
      setTimes(null);
    }
  }, [selectedTime]);

  const isSameDay = moment(selectedDate?.fullDate).isSame(moment(today), "day");

  return (
    <div
      style={{
        height: !isSameDay ? "500px" : "485px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Typography variant="body1">
          Please Choose from an available time slot below
        </Typography>
        <Box>
          {!isSameDay ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "end",
                marginBottom: 1,
              }}
            >
              <Button
                id="today-button"
                onClick={() => {
                  setSelectedDateIndex(0);
                  setCalendarValue(today);
                }}
                variant="text"
                startIcon={<Calendar />}
              >
                Today
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                height: "35px",
              }}
            ></Box>
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
              <DatePicker
                id="meeting-scheduler-datepicker"
                label="Select Month"
                views={["month", "year"]}
                value={calendarValue}
                onChange={(newValue) => {
                  if (newValue) {
                    setCalendarValue(newValue);
                  }
                }}
                disablePast
                disableClearable
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: false,
                    variant: "outlined",
                    inputProps: {
                      readOnly: true,
                    },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </Box>
      </Box>
      <Box sx={{ width: "100%", overflowX: "hidden" }}>
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          gap={3}
          mt={2}
        >
          {/* Left User Card */}
          <Box sx={{ width: { xs: "100%", md: 240 }, flexShrink: 0 }}>
            <QueryUserCard
              meetUserDetailLoading={meetUserDetailLoading}
              meetUserDetail={meetUserDetail}
            />
          </Box>
          {/* Middle Section */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Tabs */}
            <Box
              sx={{
                overflowX: "auto",
                width: "100%",
                mb: 2,
              }}
            >
              <Tabs
                value={selectedDateIndex}
                onChange={handleDateChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                TabIndicatorProps={{ style: { height: 2 } }}
                sx={{
                  ".MuiTabs-flexContainer": {
                    width: "max-content",
                  },
                  ".MuiTabs-scrollButtons": {
                    padding: 0,
                    margin: 0,
                    minWidth: "auto",
                    width: 30,
                    opacity: 1,
                    visibility: "visible",
                  },
                  ".MuiTabs-scrollButtons.Mui-disabled": {
                    opacity: 0.3,
                    visibility: "visible",
                  },
                }}
              >
                {dateData?.map((item, idx) => (
                  <Tab
                    id={`meeting-scheduler-tab-${idx}`}
                    key={idx}
                    value={idx}
                    label={
                      <Box
                        id={`meeting-scheduler-tab-${idx}`}
                        textAlign="center"
                      >
                        <Typography fontSize={13} fontWeight="500">
                          {item.date} {item.month}
                        </Typography>
                        <Typography fontSize={12} color="textSecondary">
                          {item.day}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Box>
            <Box
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              alignItems="flex-start"
              gap={2}
            >
              {/* Time Slots */}
              <Grid container spacing={1.5}>
                {availabilityslotloading ? (
                  <Box width="100%">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <Skeleton
                        key={index}
                        sx={{ marginTop: 1 }}
                        animation="pulse"
                      />
                    ))}
                  </Box>
                ) : selectedDate?.slots?.length ? (
                  selectedDate?.slots.map((slot, index) => (
                    <Grid item xs={12} sm={3} md={6} lg={3} key={slot?.id}>
                      <Button
                        id={`meeting-scheduler-slot-${index}`}
                        variant={
                          selectedTime?.id === slot?.id
                            ? "contained"
                            : "outlined"
                        }
                        sx={{
                          borderRadius: 6,
                          fontWeight: 500,
                          width: "100%",
                          height: 50,
                          ...(selectedTime?.id === slot?.id
                            ? {
                                bgcolor: "primary.main",
                                color: "#fff",
                                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                              }
                            : {
                                borderColor: "primary.main",
                                color: "#000",
                              }),
                        }}
                        onClick={() => setSelectedTime(slot)}
                      >
                        {slot?.label}
                      </Button>
                    </Grid>
                  ))
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ mt: 2, mb: 2, fontWeight: "300" }}
                    >
                      Slot not available
                    </Typography>
                  </Box>
                )}
              </Grid>
              {selectedDate?.slots?.length ? (
                <Box sx={{ width: { xs: "100%", md: 260 }, flexShrink: 0 }}>
                  <BookingDetails
                    setOpenBigModal={setOpenBigModal}
                    selectedDate={selectedDate}
                    selectedTime={selectedTime}
                    setSecondPageStep={setSecondPageStep}
                    setDescription={setDescription}
                    durationValue={durationValue}
                  />
                </Box>
              ) : null}
            </Box>
          </Box>
          {/* Right Booking Details */}
        </Box>
      </Box>
    </div>
  );
};

export default MeetingScheduler;

// Query User Info Card
const QueryUserCard = ({ meetUserDetail, meetUserDetailLoading }) => {
  const firstName = meetUserDetail?.first_name;
  const lastName = meetUserDetail?.last_name;
  const firstLetter = firstName?.[0]?.toUpperCase();
  const lastLetter = lastName?.[0]?.toUpperCase();
  const profile_pic = meetUserDetail?.profile_pic?.document;
  const randomProfileBg = getColorProfile({ firstName, lastName });

  return (
    <Card
      sx={{
        width: "100%",
        textAlign: "center",
        borderRadius: "15px",
        backgroundColor: "background.default",
        boxShadow: 2,
        border: `1.5px solid #9309FD`,
      }}
    >
      {meetUserDetailLoading ? (
        <Box
          sx={{
            minHeight: "300px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          width="100%"
        >
          <CircularProgress
            sx={{
              color: "#9309FD",
            }}
          />
        </Box>
      ) : (
        <>
          <Avatar
            alt={firstName}
            src={profile_pic !== "undefined" && `${profile_pic}`}
            sx={{
              width: 80,
              height: 80,
              mx: "auto",
              mt: 1,
              background: randomProfileBg,
            }}
          >
            {firstLetter + lastLetter}
          </Avatar>
          <CardContent sx={{ p: 0, mt: 1 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontSize={13}
              gutterBottom
            >
              Name
            </Typography>
            <Typography variant="h6" fontWeight={600} fontSize={17}>
              {firstName + " " + lastName}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontSize={13}
              mt={1}
              gutterBottom
            >
              Email
            </Typography>
            <Typography
              component="a"
              href={`mailto:${meetUserDetail?.email}`}
              sx={{
                overflowWrap: "anywhere",
                color: meetUserDetail?.email ? "#9309FD" : "text.secondary",
                textDecoration: meetUserDetail?.email ? "underline" : "none",
                pointerEvents: meetUserDetail?.email ? "auto" : "none",
                cursor: meetUserDetail?.email ? "pointer" : "default",
                fontWeight: 500,
                fontSize: 14,
                display: "inline-block",
                paddingX: "10px",
              }}
            >
              {meetUserDetail?.email ?? "N/A"}
            </Typography>
            <Divider sx={{ my: 1, borderColor: "#eee" }} />
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontSize={13}
              gutterBottom
            >
              Role
            </Typography>
            <Typography fontWeight={500} fontSize={14}>
              {meetUserDetail?.job_title ?? "N/A"}
            </Typography>
          </CardContent>
        </>
      )}
    </Card>
  );
};

// Booking Details Card
const BookingDetails = ({
  setOpenBigModal,
  selectedDate,
  selectedTime,
  setSecondPageStep,
  setDescription,
  durationValue,
}) => {
  return (
    <Card
      sx={{
        width: "100%",
        p: 2,
        borderRadius: "15px",
        backgroundColor: "background.default",
        boxShadow: 2,
        border: `1.5px solid #9309FD`,
      }}
    >
      <Typography variant="h6" gutterBottom fontSize={17}>
        Booking Details
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body1" fontWeight={500} fontSize={15}>
        Meeting Date
      </Typography>
      <Typography variant="body1" mb={1} color="textSecondary" fontSize={13}>
        {selectedDate?.date} {selectedDate?.month}, 2020
      </Typography>

      <Typography variant="body2" fontWeight={500} fontSize={15}>
        Meeting Timing
      </Typography>
      <Typography variant="body2" mb={1} color="textSecondary" fontSize={13}>
        {selectedTime?.label} ({durationValue} Minutes)
      </Typography>
      <Typography variant="body2" fontWeight={500} fontSize={15}>
        Meeting Type
      </Typography>
      <Typography variant="body2" mb={1} color="textSecondary" fontSize={13}>
        {selectedTime?.meeting_preference?.type}
      </Typography>
      {selectedTime?.meeting_preference?.location && (
        <>
          <Typography variant="body2" fontWeight={500} fontSize={15}>
            Address
          </Typography>
          <Typography
            sx={{
              overflowWrap: "anywhere",
            }}
            variant="body2"
            color="textSecondary"
            fontSize={13}
          >
            {selectedTime?.meeting_preference?.location}
          </Typography>{" "}
        </>
      )}
      <Button
        id="meeting-scheduler-description-button"
        onClick={() => {
          setDescription("");
          setOpenBigModal(false);
          setSecondPageStep(true);
        }}
        variant="contained"
        sx={{ mt: 2, bgcolor: "primary.main", color: "#fff" }}
      >
        Description (optional)
      </Button>
    </Card>
  );
};
